// What a (provider, model) pair can do about reasoning, DETECTED — and the default the
// platform applies when it can.
//
// OWNER RULINGS, 2026-09-28, verbatim, and the reason every line below exists:
//
//   "if provider + model support, it should be always default high"
//   "value > tokens(price)"
//
// So the default effort is HIGH for every lane whose provider AND model support it, and
// nothing here trades reasoning depth for tokens or latency. Do not "optimize" the
// default down: a lane that got slower gets a bigger timeout, never a lower effort
// (`docs/standards/agent-budgets.md`). The ONLY things that lower it are an explicit
// owner setting — a purpose route's `reasoning_effort` (`llm_routes`) — and the
// account's own spend caps, which bound money, not effort.
//
// SUPPORT IS DETECTED, NEVER GUESSED FROM AN ID. The capability is read off the
// provider's own API beside the price and the window (`detectKeyModelLimits`,
// `@developerz/billing`): OpenRouter's `/models` publishes `supported_parameters` and a
// `reasoning` block per model; a provider that publishes nothing is PROBED once per
// (key, model) with the exact request the platform would send, and the verdict is
// cached on the catalogue row (`byok_key_models.reasoning`). Unknown support sends
// NOTHING — a parameter a provider rejects fails the call, and "probably supported" is
// how a lane goes dark.
//
// THE TYPES OF REASONING CONTROL, per (provider, model) — what `mode` names:
//
//   effort  — a named level (`low | medium | high`, sometimes `xhigh | max`); `levels`
//             lists the ones the provider named, verbatim, when it named any;
//   budget  — a token budget for thinking (Anthropic `budget_tokens`, OpenRouter
//             `reasoning.max_tokens`); `max_budget` when the provider published one;
//   toggle  — on/off only (z.ai GLM and Kimi `thinking: {type: 'enabled'}`);
//   always  — the model reasons whatever it is sent; there is nothing to set.
//
// `returns_reasoning` says whether the reasoning TEXT comes back (OpenRouter's
// `include_reasoning`, or a probe that saw reasoning content), as opposed to being
// billed and hidden.

import { z } from 'zod';
import { REASONING_EFFORTS, type ReasoningEffort } from './reasoning-effort';

/** The kinds of reasoning control a provider exposes for a model. */
export const REASONING_MODES = ['effort', 'budget', 'toggle', 'always'] as const;
export const ReasoningModeSchema = z.enum(REASONING_MODES);
export type ReasoningMode = z.infer<typeof ReasoningModeSchema>;

/**
 * Where a capability verdict came from. `catalog` — the provider's own model list said
 * so; `probe` — a one-time call with the parameter proved it (or proved it rejected);
 * `unknown` — neither could say (a probe that hit a 429, a 5xx, a timeout), which is
 * cached too so the next refresh, not the next run, retries it.
 */
export const REASONING_CAPABILITY_SOURCES = ['catalog', 'probe', 'unknown'] as const;
export const ReasoningCapabilitySourceSchema = z.enum(REASONING_CAPABILITY_SOURCES);
export type ReasoningCapabilitySource = z.infer<typeof ReasoningCapabilitySourceSchema>;

/**
 * The stored capability — `byok_key_models.reasoning`, the fleet envelope's
 * `model_reasoning.capability`, and the key listing's `reasoning`. snake_case: it is a
 * stored shape and a wire.
 *
 * `supported: null` is UNKNOWN (only with `source: 'unknown'`), and it is not "false":
 * both send nothing, but only one of them is a fact.
 */
export const ReasoningCapabilitySchema = z
  .object({
    supported: z.boolean().nullable(),
    /** Null exactly when `supported` is not `true`. */
    mode: ReasoningModeSchema.nullable(),
    /**
     * The effort levels the provider NAMED for this model, verbatim (OpenRouter's
     * `reasoning.supported_efforts` — it includes spellings outside our vocabulary, such
     * as `none`). Null = the provider named none; a probe only proves the level it sent.
     */
    levels: z.array(z.string().min(1).max(32)).max(16).nullable(),
    /** The largest thinking budget the provider published, in tokens. Null = none published. */
    max_budget: z.number().int().positive().nullable(),
    /** Whether the reasoning text is returned. Null = unknown. */
    returns_reasoning: z.boolean().nullable(),
    /** The provider says reasoning cannot be switched off (OpenRouter `reasoning.mandatory`). */
    mandatory: z.boolean().nullable(),
    source: ReasoningCapabilitySourceSchema,
    /** When the verdict was reached (ISO-8601). */
    checked_at: z.string().datetime({ offset: true }),
  })
  .superRefine((c, ctx) => {
    if ((c.supported === true) !== (c.mode !== null)) {
      ctx.addIssue({
        code: 'custom',
        message: 'reasoning capability: `mode` is set exactly when `supported` is true',
      });
    }
    if ((c.supported === null) !== (c.source === 'unknown')) {
      ctx.addIssue({
        code: 'custom',
        message: "reasoning capability: `supported: null` is exactly `source: 'unknown'`",
      });
    }
  });
export type ReasoningCapability = z.infer<typeof ReasoningCapabilitySchema>;

/**
 * THE DEFAULT. "if provider + model support, it should be always default high" and
 * "value > tokens(price)" (owner, 2026-09-28). Not a knob, not a cost lever: the
 * override is an explicit route `reasoning_effort`, and nothing else lowers it.
 */
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'high';

/**
 * What the platform does about reasoning on one (provider, model) with no explicit
 * override — computed once per model build, and recorded on the run so the owner can
 * see it took effect.
 *
 *   apply — send `effort` in the model's `mode`. For `effort` mode, `level` is the
 *           provider's spelling actually sent (the default when the provider accepts
 *           it, else the nearest level it named — see {@link defaultReasoning}).
 *   none  — send nothing, and `why` says which of the four reasons it was.
 */
export type DefaultReasoning =
  | {
      readonly kind: 'apply';
      readonly effort: ReasoningEffort;
      readonly mode: Exclude<ReasoningMode, 'always'>;
      readonly level: string | null;
      readonly source: ReasoningCapabilitySource;
    }
  | {
      readonly kind: 'none';
      /**
       * `unknown` — no verdict yet, or an inconclusive one: nothing is sent rather than
       * risk a rejected call. `unsupported` — the provider said no. `always` — the
       * model reasons regardless. `no_level` — the provider named levels and none of
       * them is in the platform's vocabulary.
       */
      readonly why: 'unknown' | 'unsupported' | 'always' | 'no_level';
    };

/** Our levels, lowest first — the order {@link defaultReasoning} walks. */
const ORDER: ReadonlyArray<string> = REASONING_EFFORTS;

/**
 * The default for a capability: HIGH where supported, nothing otherwise. Pure.
 *
 * WHEN THE PROVIDER NAMED LEVELS AND `high` IS NOT ONE OF THEM, the nearest level ABOVE
 * it wins, else the nearest below — never a guess at a spelling the provider did not
 * name. Upward first because "value > tokens(price)": a model offering `max` and `low`
 * but no `high` runs at `max`.
 */
export function defaultReasoning(capability: ReasoningCapability | null): DefaultReasoning {
  if (capability === null || capability.supported === null) return { kind: 'none', why: 'unknown' };
  if (!capability.supported || capability.mode === null) {
    return { kind: 'none', why: 'unsupported' };
  }
  if (capability.mode === 'always') return { kind: 'none', why: 'always' };
  const effort = DEFAULT_REASONING_EFFORT;
  const base = { kind: 'apply' as const, effort, source: capability.source };
  if (capability.mode !== 'effort') return { ...base, mode: capability.mode, level: null };
  if (capability.levels === null || capability.levels.includes(effort)) {
    return { ...base, mode: 'effort', level: effort };
  }
  const at = ORDER.indexOf(effort);
  const named = ORDER.filter((l) => capability.levels?.includes(l));
  const level =
    named.find((l) => ORDER.indexOf(l) > at) ?? named.findLast((l) => ORDER.indexOf(l) < at);
  return level === undefined
    ? { kind: 'none', why: 'no_level' }
    : { ...base, mode: 'effort', level };
}

/**
 * The catalogue's capability for `model` on this key, or null when none is recorded.
 * The fourth sibling of `detectedModelLimits` / `detectedModelRates`, reading the same
 * row through the same exact-id match. A PARKED entry still answers: support is a fact
 * about the model, not a permission to run it.
 */
export function detectedReasoning(
  key: {
    readonly models?:
      | ReadonlyArray<{
          readonly model: string;
          readonly reasoning?: ReasoningCapability | null | undefined;
        }>
      | undefined;
  },
  model: string,
): ReasoningCapability | null {
  return (key.models ?? []).find((m) => m.model === model)?.reasoning ?? null;
}

/**
 * WHAT A RUN ACTUALLY SENT — the owner's evidence the default took effect, on
 * `run_provenance` and on the native lanes' started events. One object, because the
 * fields are one decision:
 *
 *   - `effort`: the effective effort, or null when nothing was sent;
 *   - `level`: the provider spelling sent in effort mode (`max` where `high` is not one);
 *   - `mode`: the control used (`effort | budget | toggle`), null when nothing was sent;
 *   - `decided_by`: `route` (an explicit `llm_routes.reasoning_effort` — the owner's
 *     override), `default` (the platform default), or `none`;
 *   - `why`: for `none`, which reason — the capability's (`unknown`, `unsupported`,
 *     `always`, `no_level`) or the provider's (`no_spelling`: the model takes a control
 *     this wire cannot send);
 *   - `capability_source`: where the capability verdict came from, null when none.
 */
export const RunReasoningSchema = z.object({
  effort: z.enum(REASONING_EFFORTS).nullable(),
  level: z.string().min(1).max(32).nullable(),
  mode: ReasoningModeSchema.nullable(),
  decided_by: z.enum(['route', 'default', 'none']),
  why: z.enum(['unknown', 'unsupported', 'always', 'no_level', 'no_spelling']).nullable(),
  capability_source: ReasoningCapabilitySourceSchema.nullable(),
});
export type RunReasoning = z.infer<typeof RunReasoningSchema>;
